import { useRef, useEffect, useState } from 'react';

interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  handleColor?: string;
  handleSize?: number;
  dividerWidth?: number;
}

export const BeforeAfterSlider = ({
  beforeImage,
  afterImage,
  handleColor = '#ffffff',
  handleSize = 30,
  dividerWidth = 2,
}: BeforeAfterSliderProps) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [imageError, setImageError] = useState<{ before: boolean; after: boolean }>({
    before: false,
    after: false,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      handleMove(e.clientX);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging && e.touches[0]) {
      handleMove(e.touches[0].clientX);
    }
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  const handleImageError = (type: 'before' | 'after') => {
    setImageError(prev => ({ ...prev, [type]: true }));
  };

  return (
    <div className="w-full">
      <div
        ref={containerRef}
        className="relative w-full aspect-video overflow-hidden rounded-lg select-none bg-gray-100"
      >
        {/* After Image (背景) */}
        {imageError.after ? (
          <div className="w-full h-full flex items-center justify-center bg-red-50">
            <div className="text-center p-4">
              <i className="ri-error-warning-line text-4xl text-red-400 mb-2"></i>
              <p className="text-sm text-red-600">After画像の読み込みに失敗しました</p>
            </div>
          </div>
        ) : (
          <img
            src={afterImage}
            alt="After"
            className="w-full h-full object-cover"
            onError={() => handleImageError('after')}
          />
        )}

        {/* Before Image Container (オーバーレイ) */}
        <div
          className="absolute top-0 left-0 h-full overflow-hidden"
          style={{
            width: `${sliderPosition}%`,
          }}
        >
          {imageError.before ? (
            <div className="w-full h-full flex items-center justify-center bg-red-50">
              <div className="text-center p-4">
                <i className="ri-error-warning-line text-4xl text-red-400 mb-2"></i>
                <p className="text-sm text-red-600">Before画像の読み込みに失敗しました</p>
              </div>
            </div>
          ) : (
            <img
              src={beforeImage}
              alt="Before"
              className="h-full object-cover absolute top-0 left-0"
              style={{
                width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%',
                maxWidth: 'none',
              }}
              onError={() => handleImageError('before')}
            />
          )}
        </div>

        {/* Divider Line */}
        <div
          className="absolute top-0 h-full pointer-events-none"
          style={{
            left: `${sliderPosition}%`,
            width: `${dividerWidth}px`,
            background: handleColor,
            transform: 'translateX(-50%)',
          }}
        />

        {/* Handle */}
        <div
          className="absolute top-1/2 -translate-y-1/2 rounded-full pointer-events-none z-10 flex items-center justify-center shadow-lg"
          style={{
            left: `${sliderPosition}%`,
            transform: 'translate(-50%, -50%)',
            width: `${handleSize}px`,
            height: `${handleSize}px`,
            background: handleColor,
          }}
        >
          <div className="flex items-center gap-0.5">
            <i className="ri-arrow-left-s-line text-gray-700 text-base"></i>
            <i className="ri-arrow-right-s-line text-gray-700 text-base"></i>
          </div>
        </div>

        {/* Slider Input */}
        <input
          type="range"
          min="0"
          max="100"
          value={sliderPosition}
          onChange={(e) => setSliderPosition(Number(e.target.value))}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
          className="absolute top-0 left-0 w-full h-full opacity-0 cursor-ew-resize m-0 z-20"
        />
      </div>

      {/* Image Load Warning */}
      {(imageError.before || imageError.after) && (
        <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-xs text-yellow-800">
            <span className="font-medium">画像が表示されない場合：</span><br />
            • 画像URLが正しいか確認<br />
            • Google Driveの共有設定が「リンクを知っている全員」になっているか確認<br />
            • ブラウザのコンソールでエラーを確認
          </p>
        </div>
      )}
    </div>
  );
};
